import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  List,
  ListItem,
  TextField,
  FormHelperText,
} from '@material-ui/core';
import { useTableRowStyles } from '../../styles/TableStyles';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { DialogMergeRequest } from '../../dialogs';
import { MergeRequestResponse } from '../../dialogs/DialogMergeRequest';
import { useApi } from '@backstage/core-plugin-api';
import { modelApiRef } from '../../../services';
import { NamespaceEditRequest } from '../../../services/ModelClient';
import { Grid } from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import UndoIcon from '@material-ui/icons/Undo';

/**
 * What the CMDB lets a namespace be called - `KubernetesNamespaceName` of its
 * schema, which is the rule the merge request is held to once it is filed:
 * lower-case letters, digits and dashes, with a dash neither first nor last.
 */
const NAMESPACE_NAME = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

/**
 * How long a namespace may be, the ceiling Kubernetes itself puts on one and
 * the CMDB repeats. The `product-` prefix counts towards it, so the name that
 * is measured is the prefixed one.
 */
const NAMESPACE_MAX_LENGTH = 63;

type EditNamespaceInput = {
  name: string;
  namespaces: string[];
  provider: string;
  id: string;
};

interface EditNamespacesDialogProps {
  openDialog: boolean;
  namespaceChanges: EditNamespaceInput[];
  productName: string;
  cmdbTeamUID: string;
  cmdbGroupName: string;
  currentUserMail: string;
  toggleDialog: () => void;
}

export const EditNamespacesDialog = (props: EditNamespacesDialogProps) => {
  const defaultErrorMessageState = {
    requestMessage: { error: false, errorMessage: '' },
  };
  const rowClasses = useTableRowStyles();
  const modelApi = useApi(modelApiRef);
  const displayTitle = `Update namespaces`;
  const {
    openDialog,
    toggleDialog,
    namespaceChanges,
    productName,
    cmdbTeamUID,
    cmdbGroupName,
    currentUserMail,
  } = props;
  const [namespaceInput, setNamespaceInput] = useState('');
  const [addedNamespaces, setAddedNamespaces] = useState<
    Record<string, string[]>
  >({});
  const [removedNamespaces, setRemovedNamespaces] = useState<
    Record<string, string[]>
  >({});
  const clusterGroupOptions = namespaceChanges
    .map((change: any) => change.clusterGroup)
    .filter((v: any, i: number, arr: any[]) => arr.indexOf(v) === i);
  const [selectedGroup, setSelectedGroup] = useState(
    clusterGroupOptions.length === 1 ? clusterGroupOptions[0] : '',
  );
  const [providerState, setProviderState] = useState(
    clusterGroupOptions.length === 1 ? namespaceChanges[0].provider : '',
  );
  const [namespacesToRemove, setNamespacesToRemove] = useState<
    EditNamespaceInput[]
  >([]);
  const [inputErrors, setInputErrors] = useState(defaultErrorMessageState);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const hasChanges =
    Object.values(addedNamespaces).some(arr => arr.length > 0) ||
    Object.values(removedNamespaces).some(arr => arr.length > 0);

  function handleNamespaceChange(e: React.ChangeEvent<HTMLInputElement>) {
    setNamespaceInput(e.target.value.replace(/^product-/, ''));
  }

  /**
   * The name the cluster would carry, which is the name the rules above are
   * about: the prefix is put on for the user, so it is the prefixed name that
   * has to hold up, not what was typed.
   */
  function prefixedNamespace(input: string) {
    const ns = input.trim();
    return ns.startsWith('product-') ? ns : `product-${ns}`;
  }

  function handleAddNamespace() {
    if (!inputsValid()) return;
    const newNs = prefixedNamespace(namespaceInput);
    namespaceChanges
      .filter((c: any) => c.clusterGroup === selectedGroup)
      .forEach((cluster: any) => {
        setAddedNamespaces(prev => ({
          ...prev,
          [cluster.id]: prev[cluster.id]?.includes(newNs)
            ? prev[cluster.id]
            : [...(prev[cluster.id] || []), newNs],
        }));
      });
    setNamespaceInput('');
    setInputErrors(defaultErrorMessageState);
  }

  async function createMergeRequest(): Promise<MergeRequestResponse> {
    const clustersInGroup = namespaceChanges.filter(
      (c: any) => c.clusterGroup === selectedGroup,
    );
    const namespaceUpdate = clustersInGroup.map((cluster: any) => {
      const current = cluster.namespaces || [];
      const added = addedNamespaces[cluster.id] || [];
      const removed = removedNamespaces[cluster.id] || [];
      const finalNamespaces = Array.from(
        new Set([...current, ...added]),
      ).filter(ns => !removed.includes(ns));
      return {
        clusterUid: cluster.id,
        namespaces: finalNamespaces,
        environment: cluster.environment,
      };
    });
    const editRequest: NamespaceEditRequest = {
      clusterGroup: selectedGroup,
      namespaceUpdate: namespaceUpdate,
      provider: providerState,
      teamCmdbKey: cmdbGroupName,
      productName: productName,
      teamUID: cmdbTeamUID,
      requestingUser: currentUserMail,
    };
    return (await modelApi.requestNamespaceUpdate(
      editRequest,
    )) as MergeRequestResponse;
  }

  function inputsValid() {
    if (
      !namespaceInput &&
      (Object.values(addedNamespaces).some(arr => arr.length > 0) ||
        Object.values(removedNamespaces).some(arr => arr.length > 0))
    ) {
      setInputErrors(defaultErrorMessageState);
      return true;
    }
    if (namespaceInput.length === 0) {
      setInputErrors({
        ...inputErrors,
        requestMessage: {
          error: true,
          errorMessage: 'This is a required field. Please fill it out.',
        },
      });
      return false;
    }
    const namespace = prefixedNamespace(namespaceInput);
    if (namespace.length > NAMESPACE_MAX_LENGTH) {
      setInputErrors({
        ...inputErrors,
        requestMessage: {
          error: true,
          errorMessage: `A namespace is at most ${NAMESPACE_MAX_LENGTH} characters long, the "product-" prefix included.`,
        },
      });
      return false;
    }
    if (!NAMESPACE_NAME.test(namespace)) {
      setInputErrors({
        ...inputErrors,
        requestMessage: {
          error: true,
          errorMessage:
            'A namespace is made of lower-case letters, digits and dashes, and does not end in a dash.',
        },
      });
      return false;
    }
    return true;
  }

  function handleTimeout() {
    setNamespacesToRemove(namespacesToRemove);
    setInputErrors(defaultErrorMessageState);
  }
  const namespaceTable = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            select
            label="Cluster Group"
            value={selectedGroup}
            onChange={e => {
              const selectedGroup = e.target.value;
              setSelectedGroup(selectedGroup);
              setAddedNamespaces({});
              setRemovedNamespaces({});
              setNamespaceInput('');
              setInputErrors(defaultErrorMessageState);
              const clusters = namespaceChanges.filter(
                (c: any) => c.clusterGroup === selectedGroup,
              );
              setProviderState(clusters[0].provider!);
            }}
            fullWidth
            variant="outlined"
            margin="dense"
            InputProps={{
              style: {
                fontSize: 18,
              },
            }}
          >
            {clusterGroupOptions.map((group: string) => (
              <option
                key={group}
                value={group}
                style={{
                  fontSize: 18,
                  paddingLeft: 16,
                  background:
                    hoveredGroup === group
                      ? 'rgba(33, 150, 243, 0.08)'
                      : undefined,
                }}
                onMouseEnter={() => setHoveredGroup(group)}
                onMouseLeave={() => setHoveredGroup(null)}
              >
                {group}
              </option>
            ))}
          </TextField>
        </Grid>
        {selectedGroup && (
          <Grid item xs={12}>
            <List>
              {namespaceChanges
                .filter((c: any) => c.clusterGroup === selectedGroup)
                .map((cluster: any) => {
                  const current = cluster.namespaces || [];
                  const added = addedNamespaces[cluster.id] || [];
                  const removed = removedNamespaces[cluster.id] || [];
                  const allNamespaces = Array.from(
                    new Set([
                      ...current,
                      ...added,
                      ...removed.filter((ns: string) => current.includes(ns)),
                    ]),
                  );
                  return (
                    <ListItem
                      key={cluster.id}
                      style={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        paddingBottom: 16,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 'bold', marginBottom: 4 }}
                      >
                        {cluster.name}
                        {` (${cluster.environment})`}
                      </Typography>
                      <List style={{ width: '100%' }}>
                        {allNamespaces.map((ns: string) => {
                          const isAdded = added.includes(ns);
                          const isExisting = current.includes(ns);
                          const isRemoved =
                            (removedNamespaces[cluster.id] || []).includes(
                              ns,
                            ) && isExisting;
                          let rowClass = rowClasses.tableRowRootDefault;
                          if (isAdded) rowClass = rowClasses.tableRowRootAdded;
                          if (isRemoved)
                            rowClass = rowClasses.tableRowRootRemoved;
                          return (
                            <ListItem
                              key={ns}
                              className={rowClass}
                              style={{
                                paddingRight: 0,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                variant="body2"
                                style={{
                                  flex: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {ns}
                                {isAdded && (
                                  <AddCircleOutlineIcon
                                    fontSize="small"
                                    style={{ marginLeft: 6 }}
                                  />
                                )}
                                {isRemoved && (
                                  <RemoveCircleOutlineIcon
                                    fontSize="small"
                                    style={{ marginLeft: 6 }}
                                  />
                                )}
                              </Typography>
                              <Button
                                size="small"
                                onClick={() => {
                                  if (isAdded) {
                                    setAddedNamespaces(prev => ({
                                      ...prev,
                                      [cluster.id]: prev[cluster.id].filter(
                                        n => n !== ns,
                                      ),
                                    }));
                                  } else if (isExisting && !isRemoved) {
                                    setRemovedNamespaces(prev => ({
                                      ...prev,
                                      [cluster.id]: prev[cluster.id]?.includes(
                                        ns,
                                      )
                                        ? prev[cluster.id]
                                        : [...(prev[cluster.id] || []), ns],
                                    }));
                                  } else if (isRemoved) {
                                    setRemovedNamespaces(prev => ({
                                      ...prev,
                                      [cluster.id]: prev[cluster.id].filter(
                                        n => n !== ns,
                                      ),
                                    }));
                                  }
                                }}
                                style={{ minWidth: 24 }}
                              >
                                {isRemoved ? (
                                  <UndoIcon fontSize="small" />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </Button>
                            </ListItem>
                          );
                        })}
                      </List>
                    </ListItem>
                  );
                })}
            </List>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <TextField
              required
              error={inputErrors.requestMessage.error}
              id="outlined-helperText"
              label="New Namespace"
              variant="outlined"
              margin="dense"
              value={namespaceInput}
              onChange={handleNamespaceChange}
              InputProps={{ style: { fontSize: 18 } }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddNamespace();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              style={{
                minWidth: 36,
                height: 40,
                padding: 0,
                alignSelf: 'center',
              }}
              size="small"
              onClick={handleAddNamespace}
            >
              <AddIcon fontSize="small" />
            </Button>
          </Box>
          <FormHelperText error={inputErrors.requestMessage.error}>
            {inputErrors.requestMessage.error
              ? inputErrors.requestMessage.errorMessage
              : 'Add a new namespace for your cluster group. The prefix "product-" will be added automatically for new namespaces.'}
          </FormHelperText>
        </Grid>
      </Grid>
    </>
  );

  return (
    <DialogMergeRequest
      displayTitle={displayTitle}
      openDialog={openDialog}
      normalContent={namespaceTable}
      hasChanges={hasChanges}
      toggleDialog={toggleDialog}
      inputsValid={inputsValid}
      handleTimeout={handleTimeout}
      createMergeRequest={createMergeRequest}
    />
  );
};
